<script lang="ts">
  import { map } from 'lodash-es'
  import { tick } from 'svelte'
  import { isNonEmptyArray } from '#app/lib/boolean_tests'
  import Flash, { type FlashState } from '#app/lib/components/flash.svelte'
  import { LogError } from '#app/lib/loggers'
  import { scrollToElement } from '#app/lib/screen'
  import type { EntitiesSortFunction } from '#app/types/entity'
  import ActorFollowersSection from '#entities/components/layouts/actor_followers_section.svelte'
  import type { ItemsByEditions } from '#entities/components/layouts/edition_actions.svelte'
  import RelativeEntitiesList from '#entities/components/layouts/relative_entities_list.svelte'
  import { hasSelectedLang } from '#entities/components/lib/claims_helpers'
  import { hasPublisher, hasPublicationYear, type WorkEditionFilters } from '#entities/components/lib/editions_list_actions_helpers'
  import { getRelativeEntitiesListLabel, getRelativeEntitiesProperties } from '#entities/components/lib/relative_entities_helpers'
  import { sortFunctionPlaceholder } from '#entities/components/lib/sort_entities_by'
  import { getPublishersByUrisFromEditions } from '#entities/components/lib/work_helpers'
  import { runEntityNavigate } from '#entities/lib/document_metadata'
  import { getAndAssignPopularity, type SerializedEntity, type SerializedEntitiesByUris } from '#entities/lib/entities'
  import Spinner from '#general/components/spinner.svelte'
  import type { SerializedItemWithUserData } from '#inventory/lib/items'
  import EntityListingsLayout from '#listings/components/entity_listings_layout.svelte'
  import type { EntityUri } from '#server/types/entity'
  import { i18n } from '#user/lib/i18n'
  import { getSubEntities } from '../lib/entities.ts'
  import BaseLayout from './base_layout.svelte'
  import HomonymDeduplicates from './deduplicate_homonyms.svelte'
  import EditionsList from './editions_list.svelte'
  import EntityInfo from './entity_info.svelte'
  import ItemsLists from './items_lists/items_lists.svelte'
  import WorkActions from './work_actions.svelte'

  let {
    entity = $bindable(),
  }: {
    entity: SerializedEntity
  } = $props()

  let showMap = $state(false)
  let itemsListsWrapperEl: HTMLElement = $state()
  let mapWrapperEl: HTMLElement = $state()
  let flash: FlashState = $state()

  const { uri, type } = entity

  let initialEditions: SerializedEntity[] = $state([])
  let publishersByUris: SerializedEntitiesByUris = $state()
  let allItems: SerializedItemWithUserData[] = $state([])
  let itemsByEditions: ItemsByEditions = $state({})
  let waitingForItems: Promise<SerializedItemWithUserData[]> = $state()

  // The sortFunctionPlaceholder is required for cases where there is only 1 edition, thus
  // EditionsListActions won't be initialized and SortEntitiesBy won't be able to initialize entitiesSortFunction
  let entitiesSortFunction: EntitiesSortFunction = $state(sortFunctionPlaceholder)

  let workEditionFilters: WorkEditionFilters = $state({})

  async function getEditionsWithPublishersAndPopularity () {
    initialEditions = await getSubEntities('work', uri)
    publishersByUris = await getPublishersByUrisFromEditions(initialEditions)
    await getAndAssignPopularity({ entities: initialEditions })
  }

  const waitingForEditions = getEditionsWithPublishersAndPopularity().catch(err => flash = err)

  const filteredEntities = $derived.by(() => {
    let _filteredEntities = initialEditions
    if (workEditionFilters.selectedLang && workEditionFilters.selectedLang !== 'all') {
      _filteredEntities = _filteredEntities.filter(hasSelectedLang(workEditionFilters.selectedLang))
    }
    if (workEditionFilters.selectedPublisher === 'unknown') {
      _filteredEntities = _filteredEntities.filter(hasPublisher('unknown'))
    } else if (workEditionFilters.selectedPublisher && workEditionFilters.selectedPublisher !== 'all') {
      _filteredEntities = _filteredEntities.filter(hasPublisher(workEditionFilters.selectedPublisher))
    }
    if (workEditionFilters.selectedPublicationYear === 'unknown') {
      _filteredEntities = _filteredEntities.filter(hasPublicationYear('unknown'))
    } else if (workEditionFilters.selectedPublicationYear && workEditionFilters.selectedPublicationYear !== 'all') {
      _filteredEntities = _filteredEntities.filter(hasPublicationYear(workEditionFilters.selectedPublicationYear))
    }
    return _filteredEntities
  })

  let sortedFilteredEditions: SerializedEntity[] = $state([])
  $effect(() => {
    entitiesSortFunction(filteredEntities)
      .then((sortedEntities: SerializedEntity[]) => {
        sortedFilteredEditions = sortedEntities
      })
      .catch(LogError)
  })

  async function showMapAndScrollToMap () {
    showMap = true
    await tick()
    scrollToElement(mapWrapperEl, { marginTop: 10, waitForRoomToScroll: false })
  }

  function scrollToItemsList () {
    scrollToElement(itemsListsWrapperEl, { marginTop: 10, waitForRoomToScroll: false })
  }

  $effect(() => {
    runEntityNavigate(entity)
  })
  const editionsUris: EntityUri[] = $derived.by(() => {
    if (isNonEmptyArray(sortedFilteredEditions)) return map(sortedFilteredEditions, 'uri')
  })
  const someEditions = $derived(sortedFilteredEditions && isNonEmptyArray(sortedFilteredEditions))
  const hasSomeInitialEditions = $derived(initialEditions && isNonEmptyArray(initialEditions))
</script>

<BaseLayout {entity}>
  {#snippet entityLayoutMain()}
    <div class="entity-layout">
      <div class="top-section">
        <div class="work-section">
          <Flash state={flash} />
          <EntityInfo {entity} />
          <WorkActions
            {entity}
            {someEditions}
            editions={sortedFilteredEditions}
            bind:allItems
            {scrollToItemsList}
            {showMapAndScrollToMap}
          />
        </div>
        <div
          class="editions-section-wrapper"
          class:no-edition={!hasSomeInitialEditions}
        >
          {#await waitingForEditions}
            <div class="loading-wrapper">
              <p class="loading">{i18n('Looking for editions…')}
                <Spinner />
              </p>
            </div>
          {:then}
            <EditionsList
              {hasSomeInitialEditions}
              {publishersByUris}
              parentEntity={entity}
              {initialEditions}
              editions={sortedFilteredEditions}
              bind:itemsByEditions
              {waitingForItems}
              bind:workEditionFilters
              bind:entitiesSortFunction
            />
          {/await}
        </div>
      </div>
      {#await waitingForEditions then}
        {#if someEditions}
          <div class="items-lists-section">
            <ItemsLists
              {editionsUris}
              bind:showMap
              bind:allItems
              bind:itemsByEditions
              bind:waitingForItems
              bind:mapWrapperEl
              bind:itemsListsWrapperEl
              {showMapAndScrollToMap}
              {workEditionFilters}
            />
          </div>
        {/if}
      {/await}
      <EntityListingsLayout {entity} />
      <div class="relatives-lists">
        {#each getRelativeEntitiesProperties(type) as property (property)}
          <RelativeEntitiesList
            {entity}
            {property}
            label={getRelativeEntitiesListLabel({ property, entity })}
          />
        {/each}
      </div>
      <ActorFollowersSection uri={entity.uri} />
      <HomonymDeduplicates {entity} />
    </div>
  {/snippet}
</BaseLayout>

<style lang="scss">
  @import "general/scss/utils";
  @import "entities/scss/relatives_lists";
  .entity-layout{
    @include display-flex(column, center);
    inline-size: 100%;
  }
  .no-edition{
    flex: none;
    inline-size: 15em;
  }
  .top-section{
    @include display-flex(row, flex-start, center);
    inline-size: 100%;
  }
  .work-section{
    @include display-flex(column, flex-start);
    flex: 1 0 0;
    margin: 0 1em;
    :global(.claims-infobox-wrapper){
      margin-block-end: 1em;
    }
    :global(.summary){
      margin-block-start: 0.5em;
    }
  }
  .items-lists-section{
    @include display-flex(column, center);
    inline-size: 100%;
    margin: 1em 0;
  }
  .loading-wrapper{
    @include display-flex(column, center);
  }
  .editions-section-wrapper{
    flex: 1;
    &.no-edition{
      flex: 0.4;
    }
  }
  .relatives-lists{
    margin-block-start: 1em;
    align-self: stretch;
    @include relatives-lists-commons;
  }
  /* Small screens */
  @media screen and (width < $small-screen){
    .work-section{
      margin-inline-start: 0;
      :global(.claims-infobox-wrapper){
        margin-block-end: 0;
      }
      :global(.summary.has-summary){
        margin-block-start: 1em;
      }
    }
    .top-section{
      @include display-flex(column, center);
    }
    .editions-section-wrapper{
      inline-size: 100%;
      margin-block-start: 1em;
    }
    .entity-layout{
      @include display-flex(column);
    }
  }
</style>
